import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getAllPostsWithStatus, deletePost } from "../services/blogService";
import { migratePosts } from "../utils/migratePosts";
import AdminLayout from "../components/AdminLayout";

export default function AdminDashboard() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateStatus, setMigrateStatus] = useState("");

  async function loadPosts() {
    try {
      const data = await getAllPostsWithStatus();
      setPosts(data);
    } catch (err) {
      console.error("Failed to load posts:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPosts();
  }, []);

  async function handleDelete(id) {
    try {
      await deletePost(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Failed to delete post:", err);
    }
  }

  async function handleMigrate() {
    setMigrating(true);
    setMigrateStatus("Starting migration...");
    try {
      const result = await migratePosts((current, total, title) => {
        setMigrateStatus(`Migrating ${current}/${total}: ${title}`);
      });
      setMigrateStatus(
        `Migration complete: ${result.migrated} posts migrated, ${result.skipped} skipped.`
      );
      if (result.migrated > 0) {
        loadPosts();
      }
    } catch (err) {
      setMigrateStatus(`Migration failed: ${err.message}`);
    } finally {
      setMigrating(false);
    }
  }

  const filteredPosts = posts.filter((p) => {
    if (filter === "published") return p.published;
    if (filter === "draft") return !p.published;
    return true;
  });

  const publishedCount = posts.filter((p) => p.published).length;
  const draftCount = posts.filter((p) => !p.published).length;

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Blog Dashboard</h1>
            <p className="text-gray-400 mt-1">
              Manage your blog posts and content
            </p>
          </div>
          <Link
            to="/admin/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-black bg-gradient-to-r from-cyber-blue to-tech-green hover:shadow-[0_0_20px_rgba(0,163,255,0.3)] transition-all"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            New Post
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => setFilter("all")}
            className={`glass rounded-xl p-4 text-left transition-all ${
              filter === "all"
                ? "border-cyber-blue/50 shadow-[0_0_12px_rgba(0,163,255,0.15)]"
                : "hover:bg-white/10"
            }`}
          >
            <p className="text-2xl font-bold">{posts.length}</p>
            <p className="text-gray-400 text-sm">Total Posts</p>
          </button>
          <button
            onClick={() => setFilter("published")}
            className={`glass rounded-xl p-4 text-left transition-all ${
              filter === "published"
                ? "border-tech-green/50 shadow-[0_0_12px_rgba(0,255,157,0.15)]"
                : "hover:bg-white/10"
            }`}
          >
            <p className="text-2xl font-bold text-tech-green">{publishedCount}</p>
            <p className="text-gray-400 text-sm">Published</p>
          </button>
          <button
            onClick={() => setFilter("draft")}
            className={`glass rounded-xl p-4 text-left transition-all ${
              filter === "draft"
                ? "border-yellow-500/50 shadow-[0_0_12px_rgba(234,179,8,0.15)]"
                : "hover:bg-white/10"
            }`}
          >
            <p className="text-2xl font-bold text-yellow-500">{draftCount}</p>
            <p className="text-gray-400 text-sm">Drafts</p>
          </button>
        </div>

        {posts.length === 0 && !loading && (
          <div className="glass rounded-2xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-white mb-1">
                  Import Existing Posts
                </h3>
                <p className="text-gray-400 text-sm">
                  No posts found. Import your existing blog posts from the local data to get started.
                </p>
                {migrateStatus && (
                  <p className="text-cyber-blue text-xs mt-2">{migrateStatus}</p>
                )}
              </div>
              <button
                onClick={handleMigrate}
                disabled={migrating}
                className="px-4 py-2 rounded-xl text-sm font-medium text-black bg-gradient-to-r from-cyber-blue to-tech-green hover:shadow-[0_0_20px_rgba(0,163,255,0.3)] transition-all disabled:opacity-50 whitespace-nowrap"
              >
                {migrating ? "Migrating..." : "Import Posts"}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-2 border-cyber-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading posts...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center">
            <p className="text-gray-400 text-lg mb-4">
              {filter === "all"
                ? "No posts yet. Create your first blog post!"
                : `No ${filter} posts found.`}
            </p>
            <Link
              to="/admin/new"
              className="inline-flex items-center gap-2 text-cyber-blue hover:underline"
            >
              Create a post →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                className="glass rounded-xl p-5 flex flex-col md:flex-row gap-4 items-start md:items-center"
              >
                {post.image && (
                  <img
                    src={post.image}
                    alt=""
                    className="w-full md:w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        post.published
                          ? "bg-tech-green/20 text-tech-green"
                          : "bg-yellow-500/20 text-yellow-500"
                      }`}
                    >
                      {post.published ? "Published" : "Draft"}
                    </span>
                    <span className="text-xs text-gray-500">{post.category}</span>
                  </div>
                  <h3 className="text-white font-semibold truncate">
                    {post.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{post.readingTime}</span>
                    {post.tags && post.tags.length > 0 && (
                      <span>
                        {post.tags.slice(0, 3).join(", ")}
                        {post.tags.length > 3 && ` +${post.tags.length - 3}`}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link
                    to={`/admin/edit/${post.id}`}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white glass hover:bg-white/10 transition-all"
                  >
                    Edit
                  </Link>
                  <Link
                    to={`/blog/${post.slug}`}
                    target="_blank"
                    className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white glass hover:bg-white/10 transition-all"
                  >
                    View
                  </Link>
                  {deleteConfirm === post.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium text-red-400 bg-red-500/20 hover:bg-red-500/30 transition-all"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(post.id)}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </AdminLayout>
  );
}
